import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Check, X, AlertTriangle } from 'lucide-react';
import type { AgentMessage as AgentMessageType } from '../../types/agent.types';

interface AgentMessageProps {
  message: AgentMessageType;
}

type MessageVariant = 'user' | 'assistant' | 'confirmation' | 'error';

function getVariant(message: AgentMessageType): MessageVariant {
  if (message.role === 'user') return 'user';
  if (message.role === 'confirmation') return 'confirmation';

  // Check if assistant message has errors
  const hasError = message.tool_results?.some(r => !r.success)
    || message.content?.startsWith('Ошибка');
  if (hasError) return 'error';

  return 'assistant';
}

const variantStyles: Record<MessageVariant, { bg: string; border: string; iconColor?: string }> = {
  user: { bg: 'var(--primary-50)', border: 'var(--primary-100)' },
  assistant: { bg: 'var(--gray-50)', border: 'var(--gray-200)' },
  confirmation: { bg: 'var(--success-50)', border: 'var(--success-200)', iconColor: 'var(--success-600)' },
  error: { bg: 'var(--error-50)', border: 'var(--error-200)', iconColor: 'var(--error-600)' },
};

const AgentMessage: React.FC<AgentMessageProps> = ({ message }) => {
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const variant = getVariant(message);
  const style = variantStyles[variant];
  const isUser = message.role === 'user';

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: isUser ? 'flex-end' : 'flex-start',
      padding: '0 var(--space-4)',
    }}>
      {/* Message bubble */}
      <div style={{
        maxWidth: '85%',
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-lg)',
        background: style.bg,
        border: `1px solid ${style.border}`,
        fontSize: 'var(--text-sm)',
        color: 'var(--gray-900)',
        lineHeight: 'var(--leading-relaxed)',
        whiteSpace: 'pre-wrap' as const,
        wordBreak: 'break-word' as const,
      }}>
        {/* Variant icon */}
        {variant === 'confirmation' && (
          <div className="flex items-center gap-1 mb-1 text-xs font-semibold text-success-700">
            <Check size={14} />
            Подтверждено
          </div>
        )}
        {variant === 'error' && (
          <div className="flex items-center gap-1 mb-1 text-xs font-semibold text-error-700">
            <AlertTriangle size={14} />
            Ошибка
          </div>
        )}
        {message.content}
      </div>

      {/* Tool calls chips */}
      {!isUser && message.tool_calls && message.tool_calls.length > 0 && (
        <button
          type="button"
          onClick={() => setToolsExpanded(!toolsExpanded)}
          className="flex items-center gap-1 mt-1 px-2-py-1 bg-gray-100 border-none rounded-md cursor-pointer text-xs text-gray-500 font-mono"
        >
          {toolsExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {message.tool_calls.length} {message.tool_calls.length === 1 ? 'инструмент' : 'инструментов'}
        </button>
      )}

      {/* Expanded tool calls with result status */}
      {toolsExpanded && message.tool_calls && (
        <div className="flex flex-col-dir gap-1 mt-1" style={{ maxWidth: '85%' }}>
          {message.tool_calls.map((tc, i) => {
            const result = message.tool_results?.[i];
            const success = result?.success;
            const failed = result && !result.success;

            return (
              <span
                key={i}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                  padding: '1px var(--space-2)',
                  background: failed ? 'var(--error-50)' : success ? 'var(--success-50)' : 'var(--gray-100)',
                  border: `1px solid ${failed ? 'var(--error-200)' : success ? 'var(--success-200)' : 'var(--gray-200)'}`,
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--text-xs)',
                  color: failed ? 'var(--error-700)' : success ? 'var(--success-700)' : 'var(--gray-600)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {failed ? <X size={10} /> : success ? <Check size={10} /> : null}
                {tc.tool_name ?? tc.name}
              </span>
            );
          })}
        </div>
      )}

      {/* Timestamp */}
      <span className="text-10 text-gray-400 mt-2px">
        {new Date(message.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
      </span>
    </div>
  );
};

export default AgentMessage;
